import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Presentation, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'show-options:active';

const PRODUCT_TYPES = [
  'IUL',
  'Term',
  'Term - Cash Back Option',
  'Whole Life',
  'Whole Life - GI',
];

// Common life-insurance benefits — agent toggles per tier. Order matches what
// agents typically present, with the most-quoted at the top.
const BENEFIT_OPTIONS = [
  'Day one coverage. No waiting period',
  'Accelerated death benefit (terminal illness)',
  'Accelerated death benefit (chronic illness)',
  'Accelerated death benefit (critical illness)',
  'Cash value accumulation',
  'Tax-free retirement income',
  'No medical exam',
  'Guaranteed acceptance',
  'Premium waiver on disability',
  'Return of premium',
  'Conversion option to permanent',
  'Level premium for life',
  'Guaranteed level death benefit',
];

const TIER_LABELS = ['Gold', 'Silver', 'Bronze'];

const TERM_LENGTHS = ['10 years', '15 years', '20 years', '25 years', '30 years'];
const TERM_PRODUCTS = new Set(['Term', 'Term - Cash Back Option']);

const emptyTier = (label) => ({
  label,
  death_benefit: '',
  monthly_premium: '',
  benefits: [],
});

const blankState = () => ({
  carrier_id: '',
  carrier_name: '',
  carrier_logo: '',
  financial_rating: '',
  product_type: '',
  term_length: '',
  client_name: '',
  agent_name: '',
  recommended_tier: null, // 0 | 1 | 2 | null
  tiers: TIER_LABELS.map(emptyTier),
});

export default function ShowOptionsBuilder() {
  const { user, getAuthHeader } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [state, setState] = useState(() => {
    // Restore last-used state from localStorage so the agent doesn't lose work
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return { ...blankState(), ...JSON.parse(saved) };
    } catch (e) { console.error('Failed to restore saved builder state:', e); }
    return blankState();
  });

  // Default agent_name to logged-in user name once
  useEffect(() => {
    if (user?.name && !state.agent_name) {
      setState(s => ({ ...s, agent_name: user.name }));
    }
  }, [user?.name, state.agent_name]);

  useEffect(() => {
    axios.get(`${API}/carriers`, getAuthHeader())
      .then(r => setCarriers((r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
      .catch(() => toast.error('Failed to load carriers'))
      .finally(() => setLoadingCarriers(false));
  }, [getAuthHeader]);

  // Persist every change to localStorage so the presentation tab can read it
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { console.error('Failed to persist builder state:', e); }
  }, [state]);

  const onCarrierChange = (carrierId) => {
    const c = carriers.find(x => x.id === carrierId);
    setState(s => ({
      ...s,
      carrier_id: carrierId,
      carrier_name: c?.name || '',
      carrier_logo: c?.logo_url || '',
    }));
  };

  const updateTier = (idx, patch) => {
    setState(s => ({
      ...s,
      tiers: s.tiers.map((t, i) => i === idx ? { ...t, ...patch } : t),
    }));
  };

  const toggleBenefit = (tierIdx, benefit) => {
    setState(s => ({
      ...s,
      tiers: s.tiers.map((t, i) => {
        // Toggling Gold (index 0) cascades the same change to Silver + Bronze.
        // Silver/Bronze can still toggle independently to remove a benefit
        // from a lower tier without affecting Gold.
        if (tierIdx === 0) {
          const goldHas = s.tiers[0].benefits.includes(benefit);
          const nextHas = !goldHas;
          if (nextHas && !t.benefits.includes(benefit)) {
            return { ...t, benefits: [...t.benefits, benefit] };
          }
          if (!nextHas && t.benefits.includes(benefit)) {
            return { ...t, benefits: t.benefits.filter(b => b !== benefit) };
          }
          return t;
        }
        if (i !== tierIdx) return t;
        const has = t.benefits.includes(benefit);
        return { ...t, benefits: has ? t.benefits.filter(b => b !== benefit) : [...t.benefits, benefit] };
      }),
    }));
  };

  const canLaunch = useMemo(() => {
    if (!state.carrier_id || !state.product_type) return false;
    return state.tiers.every(t => t.death_benefit && t.monthly_premium);
  }, [state]);

  const handleLaunch = useCallback(() => {
    if (!canLaunch) {
      toast.error('Fill carrier, product, and all three tiers first');
      return;
    }
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { console.error('Failed to persist builder state:', e); }
    const w = window.open('/show-options/present', '_blank', 'noopener,noreferrer');
    if (!w) toast.error('Popup blocked — allow popups for this site');
  }, [canLaunch, state]);

  const reset = () => {
    if (!window.confirm('Clear all fields?')) return;
    const fresh = blankState();
    if (user?.name) fresh.agent_name = user.name;
    setState(fresh);
  };

  return (
    <div className="space-y-6" data-testid="show-options-builder">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#1F8FBE]">Sales tool</div>
          <h1 className="mt-1 font-['Manrope'] text-3xl font-extrabold tracking-tight text-[#0B1437]">Show Options</h1>
          <p className="mt-1 text-sm text-[#7B8597]">Build a three-option close to screen-share with your client. Their view will be clean, full-screen, and trust-friendly.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="show-options-reset">Reset</Button>
          <Button
            onClick={handleLaunch}
            disabled={!canLaunch}
            className="gap-2 bg-[#0B1437] text-white hover:bg-[#1a2350]"
            data-testid="show-options-launch"
          >
            <Presentation className="h-4 w-4" />
            Launch Presentation
            <ExternalLink className="h-3.5 w-3.5 opacity-70" />
          </Button>
        </div>
      </div>

      {/* Top form: carrier / product / names */}
      <div className="rounded-2xl border border-[#E5EBF5] bg-white p-5 shadow-[0px_18px_40px_rgba(112,144,176,0.08)]">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs">Carrier</Label>
            <Select value={state.carrier_id} onValueChange={onCarrierChange} disabled={loadingCarriers}>
              <SelectTrigger data-testid="show-carrier">
                <SelectValue placeholder={loadingCarriers ? 'Loading…' : 'Pick a carrier'} />
              </SelectTrigger>
              <SelectContent className="max-h-[320px]">
                {carriers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Product</Label>
            <Select value={state.product_type} onValueChange={(v) => setState(s => ({ ...s, product_type: v, term_length: TERM_PRODUCTS.has(v) ? s.term_length : '' }))}>
              <SelectTrigger data-testid="show-product">
                <SelectValue placeholder="Pick a product" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCT_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {TERM_PRODUCTS.has(state.product_type) && (
            <div>
              <Label className="text-xs">Term length</Label>
              <Select value={state.term_length} onValueChange={(v) => setState(s => ({ ...s, term_length: v }))}>
                <SelectTrigger data-testid="show-term-length">
                  <SelectValue placeholder="Pick term length" />
                </SelectTrigger>
                <SelectContent>
                  {TERM_LENGTHS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">AM-Best rating <span className="text-[#A3AED0]">(optional)</span></Label>
            <Select value={state.financial_rating} onValueChange={(v) => setState(s => ({ ...s, financial_rating: v }))}>
              <SelectTrigger data-testid="show-rating">
                <SelectValue placeholder="Pick rating" />
              </SelectTrigger>
              <SelectContent>
                {['A+', 'A', 'A-', 'B+', 'B'].map(r => (
                  <SelectItem key={r} value={r}>{r} AM-Best</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Client name</Label>
            <Input
              placeholder="Jane Doe"
              value={state.client_name}
              onChange={(e) => setState(s => ({ ...s, client_name: e.target.value }))}
              data-testid="show-client-name"
            />
          </div>
        </div>
      </div>

      {/* Three tier columns */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {state.tiers.map((tier, idx) => {
          const isRec = state.recommended_tier === idx;
          return (
            <motion.div
              key={tier.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="rounded-2xl border bg-white p-5 shadow-[0px_18px_40px_rgba(112,144,176,0.08)]"
              style={{ borderColor: isRec ? '#0B1437' : '#E5EBF5' }}
              data-testid={`show-tier-${idx}`}
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-['Manrope'] text-lg font-extrabold tracking-tight text-[#0B1437]">{tier.label}</span>
                  {isRec && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#0B1437] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                      <Sparkles className="h-2.5 w-2.5" /> Recommended
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#A3AED0]">Recommend</Label>
                  <Switch
                    checked={isRec}
                    onCheckedChange={(v) => setState(s => ({ ...s, recommended_tier: v ? idx : (s.recommended_tier === idx ? null : s.recommended_tier) }))}
                    data-testid={`show-tier-${idx}-rec`}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Death benefit ($)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder="250000"
                    value={tier.death_benefit}
                    onChange={(e) => updateTier(idx, { death_benefit: e.target.value })}
                    data-testid={`show-tier-${idx}-db`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Monthly premium ($)</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="45.00"
                    value={tier.monthly_premium}
                    onChange={(e) => updateTier(idx, { monthly_premium: e.target.value })}
                    data-testid={`show-tier-${idx}-prem`}
                  />
                </div>
              </div>

              <div className="mt-5">
                <Label className="text-xs">Included benefits</Label>
                <div className="mt-2 space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                  {BENEFIT_OPTIONS.map((b) => {
                    const checked = tier.benefits.includes(b);
                    return (
                      <label
                        key={b}
                        className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-xs transition hover:bg-[#F4F7FE]"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleBenefit(idx, b)}
                          className="mt-0.5"
                          data-testid={`show-tier-${idx}-bn-${b.replace(/\s+/g, '-').slice(0, 30)}`}
                        />
                        <span className={checked ? 'text-[#0B1437]' : 'text-[#7B8597]'}>{b}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Agent name + helper */}
      <div className="rounded-2xl border border-[#E5EBF5] bg-white p-5 shadow-[0px_18px_40px_rgba(112,144,176,0.08)]">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs">Agent name (shown on footer)</Label>
            <Input
              value={state.agent_name}
              onChange={(e) => setState(s => ({ ...s, agent_name: e.target.value }))}
              data-testid="show-agent-name"
            />
          </div>
          <div className="flex items-end">
            <div className="rounded-lg bg-[#F4F7FE] p-3 text-[11px] text-[#7B8597]">
              <span className="font-semibold text-[#2B3674]">Tip:</span> When you click <em>Launch Presentation</em>, a new tab opens with the clean client view. Pick that tab in CrankWheel to share.
            </div>
          </div>
        </div>
      </div>

      {loadingCarriers && (
        <div className="flex items-center gap-2 text-xs text-[#7B8597]">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading carriers…
        </div>
      )}
    </div>
  );
}
